import { useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { ReportedPeriod } from "../interfaces/DTO/agent/mimshakGroupBy";
import { mimshakDataService } from "../services/mimshakDataService";

/**
 * Query keys for distinct periods
 */
export const distinctPeriodsKeys = {
  all: ["distinctPeriods"] as const,
  company: () => [...distinctPeriodsKeys.all, "company"] as const,
  user: (userId: number) => [...distinctPeriodsKeys.all, "user", userId] as const,
  contacts: (contactIds: number[]) => [...distinctPeriodsKeys.all, "contacts", contactIds.sort().join(",")] as const,
};

/**
 * Hook לשליפת כל התקופות הייחודיות בחברה
 * משתמש ב-React Query עם caching
 * מתרענן אוטומטית כל 30 דקות
 */
export const useDistinctPeriods = (
  enabled: boolean = true
): UseQueryResult<ReportedPeriod[], Error> => {
  return useQuery<ReportedPeriod[], Error>({
    queryKey: distinctPeriodsKeys.company(),
    queryFn: () => mimshakDataService.getDistinctReportedPeriods(),
    enabled,
    staleTime: 1000 * 60 * 30, // 30 דקות - תקופות משתנות רק בעת העלאה של mimshak
    gcTime: 1000 * 60 * 60 * 2, // 2 שעות - שמירה בזיכרון
    refetchInterval: 1000 * 60 * 30, // רענון אוטומטי כל 30 דקות
    refetchOnWindowFocus: false, // ללא רענון אוטומטי בחזרה לחלון
    retry: 2,
  });
};

/**
 * Hook לשליפת תקופות ייחודיות עבור משתמש ספציפי
 */
export const useDistinctPeriodsForUser = (
  userId: number,
  enabled: boolean = true
): UseQueryResult<ReportedPeriod[], Error> => {
  return useQuery<ReportedPeriod[], Error>({
    queryKey: distinctPeriodsKeys.user(userId),
    queryFn: () => mimshakDataService.getDistinctReportedPeriodsForUser(userId),
    enabled: enabled && !!userId,
    staleTime: 1000 * 60 * 30, // 30 דקות
    gcTime: 1000 * 60 * 10, // 10 דקות
    retry: 2,
  });
};

/**
 * Hook לשליפת תקופות ייחודיות עבור מספר contacts
 */
export const useDistinctPeriodsForContacts = (
  contactIds: number[],
  enabled: boolean = true
): UseQueryResult<ReportedPeriod[], Error> => {
  return useQuery<ReportedPeriod[], Error>({
    queryKey: distinctPeriodsKeys.contacts(contactIds),
    queryFn: () => mimshakDataService.getDistinctReportedPeriodsForContacts(contactIds),
    enabled: enabled && contactIds.length > 0,
    staleTime: 1000 * 60 * 30, // 30 דקות
    gcTime: 1000 * 60 * 10, // 10 דקות
    retry: 2,
  });
};

/**
 * Hook לרענון ידני של התקופות
 * להשתמש בו אחרי העלאה של mimshak
 */
export const useInvalidateDistinctPeriods = () => {
  const queryClient = useQueryClient();

  return {
    /**
     * מרענן את כל התקופות בחברה
     */
    invalidateCompanyPeriods: () => {
      queryClient.invalidateQueries({ queryKey: distinctPeriodsKeys.company() });
    },

    /**
     * מרענן את התקופות עבור משתמש ספציפי
     */
    invalidateUserPeriods: (userId: number) => {
      queryClient.invalidateQueries({ queryKey: distinctPeriodsKeys.user(userId) });
    },

    /**
     * מרענן את התקופות עבור מספר contacts
     */
    invalidateContactsPeriods: (contactIds: number[]) => {
      queryClient.invalidateQueries({ queryKey: distinctPeriodsKeys.contacts(contactIds) });
    },

    /**
     * מרענן את כל התקופות (company, user, contacts)
     */
    invalidateAllPeriods: () => {
      queryClient.invalidateQueries({ queryKey: distinctPeriodsKeys.all });
    },
  };
};
